import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Row, Col } from 'antd';
import { useTranslation } from 'react-i18next';
import { Parking } from '@/types/resources/property';

import CurrencyInput from '@/components/common/CurrencyInput';
import { BlockApartmentUnitSelector } from '@/components/common/BlockApartmentUnitSelector';

interface ParkingFormModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (values: Partial<Parking>) => void;
  parking?: Partial<Parking>;
}

export const ParkingFormModal: React.FC<ParkingFormModalProps> = ({
  visible,
  onCancel,
  onSubmit,
  parking
}) => {
  const { t } = useTranslation(['properties', 'common']);
  const [form] = Form.useForm();

  
  // State to store selected data from selectors
  const [selectedBlockData, setSelectedBlockData] = useState<{ id: string; name: string } | null>(null);
  const [selectedApartmentUnitData, setSelectedApartmentUnitData] = useState<{ id: string; name: string } | null>(null);

  // Reset form when parking changes
  useEffect(() => {
    if (visible) {
      form.resetFields();
      if (parking) {
        form.setFieldsValue({
          ...parking,
          blockId: parking.block?.id,
          apartmentUnitId: parking.apartmentUnit?.id
        });
        
        // Set selected data for existing parking
        if (parking.block) {
          setSelectedBlockData({ 
            id: parking.block.id, 
            name: parking.block.name 
          });
        }
        if (parking.apartmentUnit) {
          setSelectedApartmentUnitData({ 
            id: parking.apartmentUnit.id, 
            name: parking.apartmentUnit.number 
          });
        }
      } else {
        // Reset selected data for new parking
        setSelectedBlockData(null);
        setSelectedApartmentUnitData(null);
      }
    }
  }, [visible, parking, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const { blockId, apartmentUnitId, ...rest } = values;
      
      // Transform the form values to match the expected structure using selected data
      const transformedValues: any = {
        ...rest,
      };

      // Only add block if it's selected
      if (selectedBlockData) {
        transformedValues.block = selectedBlockData;
      }

      // Only add apartment unit if it's selected - transform to match Parking type
      if (selectedApartmentUnitData) {
        transformedValues.apartmentUnit = {
          id: selectedApartmentUnitData.id,
          number: selectedApartmentUnitData.name // Use 'name' as 'number' for Parking type
        };
      }

      onSubmit(transformedValues);
      form.resetFields();
      
      // Reset selected data
      setSelectedBlockData(null);
      setSelectedApartmentUnitData(null);
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  return (
    <Modal
      title={parking ? t('properties:parkings.actions.edit') : t('properties:parkings.actions.add')}
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          ...parking,
          blockId: parking?.block?.id,
          apartmentUnitId: parking?.apartmentUnit?.id
        }}
      >
        <Form.Item
          name="number"
          label={t('properties:parkings.fields.number')}
          rules={[{ required: true, message: t('common:validation.fieldRequired') }]}
        >
          <Input />
        </Form.Item>

        {/* Block and Apartment Unit Selection */}
        <BlockApartmentUnitSelector
          onBlockDataChange={setSelectedBlockData}
          onApartmentUnitDataChange={setSelectedApartmentUnitData}
        />

        <Form.Item
          name="type"
          label={t('properties:parkings.fields.type')}
          rules={[{ required: true, message: t('common:validation.fieldRequired') }]}
        >
          <Select
            placeholder={t('properties:parkings.fields.type')}
            options={[
              { value: 'car', label: t('properties:parkings.types.car') },
              { value: 'bike', label: t('properties:parkings.types.bike') },
              { value: 'visitor', label: t('properties:parkings.types.visitor') }
            ]}
          />
        </Form.Item>

        <Form.Item
          name="status"
          label={t('properties:parkings.fields.status')}
          rules={[{ required: true, message: t('common:validation.fieldRequired') }]}
        >
          <Select
            placeholder={t('properties:parkings.fields.status')}
            options={[
              { value: 'available', label: t('properties:parkings.status.available') },
              { value: 'occupied', label: t('properties:parkings.status.occupied') },
              { value: 'maintenance', label: t('properties:parkings.status.maintenance') }
            ]}
          />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="pricing"
              label={t('properties:parkings.fields.pricing.amount')}
              rules={[{ required: true, message: t('common:validation.fieldRequired') }]}
            >
              <CurrencyInput />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="period"
              label={t('properties:parkings.fields.pricing.period')}
              rules={[{ required: true, message: t('common:validation.fieldRequired') }]}
            >
              <Select>
                <Select.Option value="monthly">{t('properties:parkings.pricing.period.monthly')}</Select.Option>
                <Select.Option value="yearly">{t('properties:parkings.pricing.period.yearly')}</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="description"
          label={t('common.description')}
        >
          <Input.TextArea rows={4} />
        </Form.Item>
      </Form>
    </Modal>
  );
}; 